import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api.client';
import type { ApiResponse } from '@/types/api.types';

interface UsageSummary {
  total_requests: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
}

interface AssistantUsageItem {
  assistantId: string;
  name: string;
  slug: string;
  requests: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number;
}

interface UserUsageItem {
  userId: string;
  displayName: string;
  email: string;
  requests: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number;
}

export type { UsageSummary, AssistantUsageItem, UserUsageItem };

const buildParams = (dateFrom?: string, dateTo?: string) => {
  const params: Record<string, string> = {};
  if (dateFrom) params.from = dateFrom;
  if (dateTo) params.to = dateTo;
  return params;
};

export const useUsageSummary = (dateFrom?: string, dateTo?: string, enabled = true) => {
  return useQuery({
    queryKey: ['assistant-usage', 'summary', dateFrom, dateTo],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<UsageSummary>>(
        '/admin/assistants/usage/summary',
        { params: buildParams(dateFrom, dateTo) },
      );
      return data.data!;
    },
    staleTime: 30_000,
    enabled,
  });
};

export const useUsageByAssistant = (dateFrom?: string, dateTo?: string, enabled = true) => {
  return useQuery({
    queryKey: ['assistant-usage', 'by-assistant', dateFrom, dateTo],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<AssistantUsageItem[]>>(
        '/admin/assistants/usage/by-assistant',
        { params: buildParams(dateFrom, dateTo) },
      );
      return data.data!;
    },
    staleTime: 30_000,
    enabled,
  });
};

export const useUsageByUser = (dateFrom?: string, dateTo?: string, enabled = true) => {
  return useQuery({
    queryKey: ['assistant-usage', 'by-user', dateFrom, dateTo],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<UserUsageItem[]>>(
        '/admin/assistants/usage/by-user',
        { params: buildParams(dateFrom, dateTo) },
      );
      return data.data!;
    },
    staleTime: 30_000,
    enabled,
  });
};
